import React, { useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import APP_CONFIG, { TIER_ORDER, isIncludedInTier, formatCapacity } from '../config/appConfig';
import { makeMoneyFormatter } from '../lib/pricing';
import './PDFExport.css';

const PDFExport = ({
    revenue,
    tierCosts,
    recommendedTier,
    requirements,
    selectedPlatforms,
    selectedFeatures,
    pricingMode,
    revenueModel,
    currency,
    formatCurrency,
    billingType,
    monthlyGrowthRate,
    // User inputs
    videoAssets,
    avgDuration,
    totalPlays,
    avgWatchTime,
    videoQuality,
    estimatedSubscribers,
    subscribers,
    avgSpend,
    impressions,
    cpm,
    transactions,
    avgPrice,
    appDiscount
}) => {

    // Client information modal state
    const [showClientModal, setShowClientModal] = useState(false);
    const [clientInfo, setClientInfo] = useState({
        clientName: '',
        companyName: '',
        email: '',
        phone: ''
    });

    const handleGeneratePDF = () => {
        setShowClientModal(true);
    };

    const generatePDF = async () => {
        if (!clientInfo.clientName || !clientInfo.companyName) {
            alert('Please fill in client name and company name.');
            return;
        }

        setShowClientModal(false);

        // Create a temporary div for PDF content
        const pdfContent = document.createElement('div');
        pdfContent.innerHTML = getPDFContent();
        pdfContent.className = 'pdf-content';

        // Add to DOM temporarily
        document.body.appendChild(pdfContent);

        try {
            // Wait for fonts and images to load
            await new Promise(resolve => setTimeout(resolve, 500));

            // Generate PDF using html2canvas and jsPDF with better settings
            const canvas = await html2canvas(pdfContent, {
                scale: 1.5,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                width: 800,
                height: pdfContent.scrollHeight,
                scrollX: 0,
                scrollY: 0,
                windowWidth: 800,
                windowHeight: pdfContent.scrollHeight
            });

            const imgData = canvas.toDataURL('image/png', 0.95);
            const pdf = new jsPDF('p', 'mm', 'a4');

            const imgWidth = 210; // A4 width in mm
            const pageHeight = 295; // A4 height in mm - leaving margin for header/footer
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 0;

            // Add first page
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            // Add additional pages if needed
            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            // Save the PDF with client name
            const fileName = `inoRain_OTT_Proposal_${clientInfo.companyName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
            pdf.save(fileName);

        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        } finally {
            // Remove temporary element
            document.body.removeChild(pdfContent);
        }
    };

    const getPDFContent = () => {
        const currentDate = new Date().toLocaleDateString();
        const chosenTier = recommendedTier || 'contentGuru';
        const chosen = tierCosts[chosenTier];

        const selectedPlatformList = Object.entries(selectedPlatforms || {})
            .filter(([key, on]) => on && APP_CONFIG.platforms[key])
            .map(([key]) => [key, APP_CONFIG.platforms[key]]);
        const selectedFeatureList = Object.entries(selectedFeatures || {})
            .filter(([key, on]) => on && APP_CONFIG.features[key] && !APP_CONFIG.features[key].core)
            .map(([key]) => [key, APP_CONFIG.features[key]]);

        // Calculate projections
        const projections = calculateProjections();

        return `
            <div class="pdf-proposal">
                <!-- Header -->
                <div class="pdf-header">
                    <div class="header-brand">
                        <div class="inorain-logo">
                            <div class="logo-symbol">🌧️</div>
                            <div class="logo-text">
                                <h1>inoRain</h1>
                                <p>Professional OTT Solutions</p>
                            </div>
                        </div>
                        <div class="proposal-title">
                            <h2>OTT Streaming Platform Proposal</h2>
                            <p>Prepared for <strong>${clientInfo.companyName}</strong></p>
                        </div>
                    </div>
                    <div class="proposal-meta">
                        <div class="client-info">
                            <h3>Client Information</h3>
                            <p><strong>Contact:</strong> ${clientInfo.clientName}</p>
                            <p><strong>Company:</strong> ${clientInfo.companyName}</p>
                            ${clientInfo.email ? `<p><strong>Email:</strong> ${clientInfo.email}</p>` : ''}
                            ${clientInfo.phone ? `<p><strong>Phone:</strong> ${clientInfo.phone}</p>` : ''}
                        </div>
                        <div class="proposal-date">
                            <p><strong>Proposal Date:</strong> ${currentDate}</p>
                            <p><strong>Currency:</strong> ${currency}</p>
                            <p><strong>Billing:</strong> ${billingType === 'yearly' ? 'Annual' : 'Monthly'}</p>
                            <p><strong>Valid Until:</strong> ${new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                        </div>
                    </div>
                </div>

                <!-- Executive Summary -->
                <div class="pdf-section">
                    <h2>Executive Summary</h2>
                    <div class="summary-grid">
                        <div class="summary-item">
                            <h3>Revenue Model</h3>
                            <p>${getRevenueModelDescription(revenueModel)}</p>
                            <div class="revenue-details">
                                ${getRevenueDetails()}
                            </div>
                        </div>
                        <div class="summary-item">
                            <h3>Monthly Revenue</h3>
                            <p class="revenue-amount">${formatCurrency(revenue)}</p>
                        </div>
                    </div>
                </div>

                <!-- Platform Requirements -->
                <div class="pdf-section">
                    <h2>Platform Requirements</h2>
                    <div class="requirements-grid">
                        <div class="req-item">
                            <h4>Content Library</h4>
                            <p>${videoAssets.toLocaleString()} video assets</p>
                            <p>Average duration: ${avgDuration} minutes</p>
                            <p>Video quality: ${videoQuality}</p>
                        </div>
                        <div class="req-item">
                            <h4>Usage Metrics</h4>
                            <p>Concurrent users: ${totalPlays.toLocaleString()}</p>
                            <p>Average watch time: ${avgWatchTime} minutes</p>
                        </div>
                        <div class="req-item">
                            <h4>Infrastructure Needs</h4>
                            <p>Storage required: ${requirements.storage.toFixed(2)} TB</p>
                            <p>Bandwidth required: ${requirements.bandwidth.toFixed(2)} TB/month</p>
                        </div>
                    </div>
                </div>

                <!-- Platform Features -->
                <div class="pdf-section">
                    <h2>Platform Features Included</h2>
                    <div class="features-grid">
                        <div class="feature-category">
                            <h3>🎥 Content Management</h3>
                            <ul>
                                <li>Unlimited video uploads</li>
                                <li>Multi-format support (MP4, HLS, DASH)</li>
                                <li>Automatic transcoding</li>
                                <li>Content categorization & tagging</li>
                                <li>Bulk upload capabilities</li>
                            </ul>
                        </div>
                        <div class="feature-category">
                            <h3>👥 User Management</h3>
                            <ul>
                                <li>User registration & authentication</li>
                                <li>Profile management</li>
                                <li>Subscription management</li>
                                <li>Parental controls</li>
                                <li>Watch history & favorites</li>
                            </ul>
                        </div>
                        <div class="feature-category">
                            <h3>💰 Monetization</h3>
                            <ul>
                                <li>Multiple revenue models (SVOD, AVOD, TVOD)</li>
                                <li>Payment gateway integration</li>
                                <li>Subscription plans management</li>
                                <li>Ad management system</li>
                                <li>Analytics & reporting</li>
                            </ul>
                        </div>
                        <div class="feature-category">
                            <h3>📱 Multi-Platform Support</h3>
                            <ul>
                                <li>Web application (included)</li>
                                <li>Mobile apps (iOS & Android)</li>
                                <li>Smart TV applications</li>
                                <li>Desktop applications</li>
                                <li>API access for custom integrations</li>
                            </ul>
                        </div>
                        <div class="feature-category">
                            <h3>🔧 Technical Features</h3>
                            <ul>
                                <li>Global CDN delivery</li>
                                <li>Adaptive bitrate streaming</li>
                                <li>DRM protection</li>
                                <li>Live streaming capabilities</li>
                                <li>White-label customization</li>
                            </ul>
                        </div>
                        <div class="feature-category">
                            <h3>📊 Analytics & Insights</h3>
                            <ul>
                                <li>Real-time viewership analytics</li>
                                <li>Revenue tracking</li>
                                <li>User engagement metrics</li>
                                <li>Content performance reports</li>
                                <li>Custom dashboard creation</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <!-- Selected Applications -->
                ${selectedPlatformList.length > 0 ? `
                <div class="pdf-section">
                    <h2>Selected Platforms</h2>
                    <p class="section-lead">
                        ${isLegacy
            ? 'Legacy pricing: app development is billed once, up front.'
            : `Apps are included by tier. On <strong>${APP_CONFIG.packages[chosenTier].name}</strong>, anything not bundled is ${money(150)}/month.`}
                    </p>
                    <div class="apps-list">
                        ${selectedPlatformList.map(([key, platform]) => {
            const included = isIncludedInTier(platform, chosenTier);
            const legacyApp = APP_CONFIG.legacyAppBundles[key];
            let priceLabel;
            if (isLegacy) {
                const base = legacyApp ? legacyApp.price : 0;
                priceLabel = base === 0
                    ? 'Included'
                    : appDiscount > 0
                        ? `<span class="original-price">${money(base)}</span> ${money(base * (1 - appDiscount / 100))} one-time <span class="discount-badge">${appDiscount}% OFF</span>`
                        : `${money(base)} one-time`;
            } else {
                priceLabel = platform.price === 0 || included
                    ? '<span class="included-tag">Included in tier</span>'
                    : `${money(platform.price)} / month`;
            }
            return `
                                <div class="app-item">
                                    <span class="app-icon">${platform.icon}</span>
                                    <div class="app-details">
                                        <h4>${platform.name}</h4>
                                        <p>${platform.includedFrom
                    ? `Included from ${APP_CONFIG.packages[platform.includedFrom].name}`
                    : 'Available as an add-on on any tier'}</p>
                                        <span class="app-price">${priceLabel}</span>
                                    </div>
                                </div>
                            `;
        }).join('')}
                    </div>
                </div>
                ` : ''}

                ${selectedFeatureList.length > 0 ? `
                <div class="pdf-section">
                    <h2>Selected Feature Add-ons</h2>
                    <div class="apps-list">
                        ${selectedFeatureList.map(([key, feature]) => {
            const included = isIncludedInTier(feature, chosenTier);
            return `
                                <div class="app-item">
                                    <span class="app-icon">${feature.icon || '⭐'}</span>
                                    <div class="app-details">
                                        <h4>${feature.name}</h4>
                                        <p>${feature.description || ''} — included from ${APP_CONFIG.packages[feature.includedFrom].name}</p>
                                        <span class="app-price">${included
                    ? '<span class="included-tag">Included in tier</span>'
                    : `${money(feature.price)} / month`}</span>
                                    </div>
                                </div>
                            `;
        }).join('')}
                    </div>
                </div>
                ` : ''}

                <!-- Pricing Comparison -->
                <div class="pdf-section">
                    <h2>Tier Comparison &amp; Pricing</h2>
                    <p class="section-lead">
                        One platform, four tiers. Monthly, or yearly at
                        ${Math.round(APP_CONFIG.billing.yearlyDiscount * 100)}% off. No upfront cost.
                    </p>
                    <div class="pricing-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Plan</th>
                                    ${TIER_ORDER.map(t => `<th class="${t === chosenTier ? 'recommended-col' : ''}">
                                        ${APP_CONFIG.packages[t].name}${APP_CONFIG.packages[t].popular ? ' ★' : ''}
                                    </th>`).join('')}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Plan cost</td>
                                    ${TIER_ORDER.map(t => `<td>${APP_CONFIG.packages[t].custom
            ? 'Contact us'
            : money(APP_CONFIG.packages[t].price) + ' / mo'}</td>`).join('')}
                                </tr>
                                <tr>
                                    <td>Yearly (−${Math.round(APP_CONFIG.billing.yearlyDiscount * 100)}%)</td>
                                    ${TIER_ORDER.map(t => `<td>${APP_CONFIG.packages[t].custom
            ? 'Contact us'
            : money(APP_CONFIG.packages[t].price * 12 * (1 - APP_CONFIG.billing.yearlyDiscount)) + ' / yr'}</td>`).join('')}
                                </tr>
                                <tr>
                                    <td>Bandwidth / month</td>
                                    ${TIER_ORDER.map(t => `<td>${formatCapacity(APP_CONFIG.packages[t].bandwidth)}</td>`).join('')}
                                </tr>
                                <tr>
                                    <td>Storage</td>
                                    ${TIER_ORDER.map(t => `<td>${formatCapacity(APP_CONFIG.packages[t].storage)}</td>`).join('')}
                                </tr>
                                <tr>
                                    <td>Approx. active subscribers</td>
                                    ${TIER_ORDER.map(t => {
            const v = APP_CONFIG.packages[t].activeSubscribers;
            return `<td>${v === Infinity ? 'Custom' : '~' + v.toLocaleString()}</td>`;
        }).join('')}
                                </tr>
                                <tr>
                                    <td>Fits your requirements</td>
                                    ${TIER_ORDER.map(t => `<td class="${tierCosts[t].canHandle ? 'suitable' : 'insufficient'}">
                                        ${tierCosts[t].canHandle ? '✓ Suitable' : '✗ Needs overage'}
                                    </td>`).join('')}
                                </tr>
                                ${!isLegacy ? `
                                <tr>
                                    <td>Platform add-ons</td>
                                    ${TIER_ORDER.map(t => `<td>${tierCosts[t].platformAddOnCost > 0
            ? money(tierCosts[t].platformAddOnCost) + ' / mo'
            : 'All included'}</td>`).join('')}
                                </tr>` : `
                                <tr>
                                    <td>One-time app costs</td>
                                    ${TIER_ORDER.map(t => `<td>${money(tierCosts[t].oneTimeAppCosts)}</td>`).join('')}
                                </tr>`}
                                <tr>
                                    <td>Feature add-ons</td>
                                    ${TIER_ORDER.map(t => `<td>${tierCosts[t].featureAddOnCost > 0
            ? money(tierCosts[t].featureAddOnCost) + ' / mo'
            : 'All included'}</td>`).join('')}
                                </tr>
                                <tr>
                                    <td>Usage-based add-ons</td>
                                    ${TIER_ORDER.map(t => `<td>${tierCosts[t].usageCost > 0
            ? money(tierCosts[t].usageCost) + ' / mo'
            : '—'}</td>`).join('')}
                                </tr>
                                <tr>
                                    <td>Capacity overage</td>
                                    ${TIER_ORDER.map(t => `<td>${tierCosts[t].overageCost > 0
            ? money(tierCosts[t].overageCost) + ' / mo'
            : '—'}</td>`).join('')}
                                </tr>
                                <tr>
                                    <td><strong>Total Monthly Cost</strong></td>
                                    ${TIER_ORDER.map(t => `<td class="${t === chosenTier ? 'recommended-col' : ''}"><strong>${tierCosts[t].isCustom
            ? 'Contact us'
            : money(tierCosts[t].monthlyRecurringCost) + ' / mo'}</strong></td>`).join('')}
                                </tr>
                                <tr>
                                    <td><strong>Monthly Profit</strong></td>
                                    ${TIER_ORDER.map(t => {
            if (tierCosts[t].isCustom) return '<td>—</td>';
            const profit = revenue - tierCosts[t].monthlyRecurringCost;
            return `<td class="${profit >= 0 ? 'profit' : 'loss'}"><strong>${money(profit)}</strong></td>`;
        }).join('')}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p class="recommendation-line">
                        <strong>Recommended:</strong> ${APP_CONFIG.packages[chosenTier].name}
                        ${chosen.isCustom
            ? ' — your requirements exceed the published tiers, so this needs a custom quote.'
            : ` at ${money(chosen.monthlyRecurringCost)} per month, all in.`}
                    </p>
                    <p class="upgrade-line">
                        Upgrading usually wins: a higher tier bundles several platforms with more
                        features and capacity, so adding platforms one by one rarely beats moving up.
                        Bandwidth and storage beyond your tier are billed per GB
                        (${rate(APP_CONFIG.overage.bandwidth.pricePerGB)}/GB transfer,
                        ${rate(APP_CONFIG.overage.storage.pricePerGB)}/GB storage).
                    </p>
                </div>

                <!-- 4-Quarter Projections -->
                <div class="pdf-section">
                    <h2>4-Quarter Revenue Projections</h2>
                    <p class="projection-note">Based on ${monthlyGrowthRate}% monthly growth rate. Quarter columns are three-month totals.</p>
                    <div class="projections-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Quarter</th>
                                    <th>Revenue</th>
                                    ${pricedTiers.map(t => `<th>${APP_CONFIG.packages[t].name} Profit</th>`).join('')}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Monthly</strong></td>
                                    <td>${money(revenue)}</td>
                                    ${pricedTiers.map(t => {
            const profit = revenue - tierCosts[t].monthlyRecurringCost;
            return `<td class="${profit >= 0 ? 'profit' : 'loss'}">${money(profit)}</td>`;
        }).join('')}
                                </tr>
                                ${projections.quarterly.map(projection => `
                                    <tr>
                                        <td><strong>${projection.quarter}</strong></td>
                                        <td>${money(projection.revenue)}</td>
                                        ${pricedTiers.map(t => `<td class="${projection.tiers[t].profit >= 0 ? 'profit' : 'loss'}">
                                            ${money(projection.tiers[t].profit)}
                                        </td>`).join('')}
                                    </tr>
                                `).join('')}
                                <tr class="annual-row">
                                    <td><strong>Annual Total</strong></td>
                                    <td><strong>${money(projections.annual.revenue)}</strong></td>
                                    ${pricedTiers.map(t => `<td class="${projections.annual.tiers[t].profit >= 0 ? 'profit' : 'loss'}">
                                        <strong>${money(projections.annual.tiers[t].profit)}</strong>
                                    </td>`).join('')}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Implementation Timeline -->
                <div class="pdf-section">
                    <h2>Implementation Timeline</h2>
                    <div class="timeline">
                        <div class="timeline-item">
                            <h4>Week 1-2: Setup & Configuration</h4>
                            <ul>
                                <li>Platform setup and configuration</li>
                                <li>Branding and customization</li>
                                <li>Payment gateway integration</li>
                            </ul>
                        </div>
                        <div class="timeline-item">
                            <h4>Week 3-4: Content & Apps</h4>
                            <ul>
                                <li>Content library migration</li>
                                <li>App development and testing</li>
                                <li>User management setup</li>
                            </ul>
                        </div>
                        <div class="timeline-item">
                            <h4>Week 5-6: Testing & Launch</h4>
                            <ul>
                                <li>Quality assurance testing</li>
                                <li>Performance optimization</li>
                                <li>Go-live and launch support</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <!-- Support & Services -->
                <div class="pdf-section">
                    <h2>Support & Services Included</h2>
                    <div class="support-grid">
                        <div class="support-item">
                            <h4>24/7 Technical Support</h4>
                            <p>Round-the-clock monitoring and support for your platform</p>
                        </div>
                        <div class="support-item">
                            <h4>99.9% Uptime SLA</h4>
                            <p>Guaranteed service level with redundant infrastructure</p>
                        </div>
                        <div class="support-item">
                            <h4>Regular Updates</h4>
                            <p>Platform updates and new feature rollouts</p>
                        </div>
                        <div class="support-item">
                            <h4>Training & Documentation</h4>
                            <p>Comprehensive training and documentation for your team</p>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="pdf-footer">
                    <div class="footer-brand">
                        <div class="inorain-footer-logo">
                            <span class="logo-symbol">🌧️</span>
                            <span class="company-name">inoRain</span>
                        </div>
                        <p class="tagline">Empowering Your Streaming Success</p>
                    </div>
                    <div class="footer-content">
                        <p><strong>This proposal is valid for 30 days from the date of issue.</strong></p>
                        <p>Contact us for questions or to proceed with implementation.</p>
                        <div class="contact-info">
                            <div class="contact-row">
                                <span>📧 Email: sales@inorain.com</span>
                                <span>📞 Phone: +1 (555) 123-RAIN</span>
                            </div>
                            <div class="contact-row">
                                <span>🌐 Website: www.inorain.com</span>
                                <span>💬 Support: support@inorain.com</span>
                            </div>
                        </div>
                    </div>
                    <div class="footer-note">
                        <p><em>Thank you for considering inoRain for your OTT streaming platform needs. We look forward to partnering with ${clientInfo.companyName} to deliver exceptional streaming experiences.</em></p>
                    </div>
                </div>
            </div>
        `;
    };

    // Helper functions
    // Costing is done once in PricingCalculator and handed down via tierCosts,
    // so there is no second implementation to drift out of step with it.
    const pricedTiers = TIER_ORDER.filter(t => !APP_CONFIG.packages[t].custom);
    const isLegacy = pricingMode === 'legacy';

    // Every figure in the proposal is a plain period amount, so formatting must
    // not re-apply the yearly multiplier.
    const money = (amount) => formatCurrency(amount, false);

    // Whole-dollar formatting would print the per-GB rates as "$0".
    const rate = makeMoneyFormatter(currency);

    const calculateProjections = () => {
        const growthMultiplier = 1 + (monthlyGrowthRate / 100);
        const quarters = getQuarterNames();

        const projections = quarters.map((quarter, index) => {
            const quarterlyRevenue = revenue * Math.pow(growthMultiplier, index * 3) * 3;
            const isFirstQuarter = index === 0;

            const tiers = {};
            pricedTiers.forEach(tierKey => {
                const costs = tierCosts[tierKey];
                const totalCost = (costs.monthlyRecurringCost * 3) + (isFirstQuarter ? costs.oneTimeAppCosts : 0);
                tiers[tierKey] = { totalCost, profit: quarterlyRevenue - totalCost };
            });

            return { quarter: quarter.fullName, revenue: quarterlyRevenue, isFirstQuarter, tiers };
        });

        const annual = {
            revenue: projections.reduce((sum, p) => sum + p.revenue, 0),
            tiers: {}
        };
        pricedTiers.forEach(tierKey => {
            annual.tiers[tierKey] = {
                totalCost: projections.reduce((sum, p) => sum + p.tiers[tierKey].totalCost, 0),
                profit: projections.reduce((sum, p) => sum + p.tiers[tierKey].profit, 0)
            };
        });

        return { quarterly: projections, annual };
    };

    const getQuarterNames = () => {
        const currentDate = new Date();
        const quarterNames = ['Q1', 'Q2', 'Q3', 'Q4'];
        const quarters = [];

        for (let i = 0; i < 4; i++) {
            const currentQuarter = Math.floor(currentDate.getMonth() / 3);
            const quarterIndex = (currentQuarter + i) % 4;
            const year = currentDate.getFullYear() + Math.floor((currentQuarter + i) / 4);
            quarters.push({
                name: quarterNames[quarterIndex],
                year: year,
                fullName: `${quarterNames[quarterIndex]} ${year}`
            });
        }
        return quarters;
    };

    const getRevenueModelDescription = (model) => {
        const descriptions = {
            'SVOD': 'Subscription Video on Demand - Monthly/yearly subscriptions',
            'AVOD': 'Advertising Video on Demand - Revenue from advertisements',
            'TVOD': 'Transactional Video on Demand - Pay per view/rent/purchase',
            'HYBRID': 'Hybrid model combining subscription and advertising revenue'
        };
        return descriptions[model] || 'Custom revenue model';
    };

    const getRevenueDetails = () => {
        let details = '';
        if (revenueModel === 'SVOD' || revenueModel === 'HYBRID') {
            details += `<p>Subscribers: ${subscribers.toLocaleString()} × ${formatCurrency(avgSpend)} = ${formatCurrency(subscribers * avgSpend)}</p>`;
        }
        if (revenueModel === 'AVOD' || revenueModel === 'HYBRID') {
            details += `<p>Impressions: ${(impressions / 1000).toLocaleString()}K × ${formatCurrency(cpm)} CPM = ${formatCurrency((impressions / 1000) * cpm)}</p>`;
        }
        if (revenueModel === 'TVOD' || revenueModel === 'HYBRID') {
            details += `<p>Transactions: ${transactions.toLocaleString()} × ${formatCurrency(avgPrice)} = ${formatCurrency(transactions * avgPrice)}</p>`;
        }
        return details;
    };

    return (
        <>
            <button className="pdf-export-button" onClick={handleGeneratePDF}>
                📄 Export PDF Proposal
            </button>

            {/* Client Information Modal */}
            {showClientModal && (
                <div className="client-modal-overlay" onClick={() => setShowClientModal(false)}>
                    <div className="client-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Client Information</h3>
                            <p>Please provide client details for the personalized proposal</p>
                        </div>

                        <div className="modal-content">
                            <div className="form-group">
                                <label>
                                    Client Name *
                                    <input
                                        type="text"
                                        value={clientInfo.clientName}
                                        onChange={(e) => setClientInfo({ ...clientInfo, clientName: e.target.value })}
                                        placeholder="Enter client's full name"
                                        required
                                    />
                                </label>
                            </div>

                            <div className="form-group">
                                <label>
                                    Company Name *
                                    <input
                                        type="text"
                                        value={clientInfo.companyName}
                                        onChange={(e) => setClientInfo({ ...clientInfo, companyName: e.target.value })}
                                        placeholder="Enter company name"
                                        required
                                    />
                                </label>
                            </div>

                            <div className="form-group">
                                <label>
                                    Email Address
                                    <input
                                        type="email"
                                        value={clientInfo.email}
                                        onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                                        placeholder="client@company.com"
                                    />
                                </label>
                            </div>

                            <div className="form-group">
                                <label>
                                    Phone Number
                                    <input
                                        type="tel"
                                        value={clientInfo.phone}
                                        onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                                        placeholder="+1 (555) 123-4567"
                                    />
                                </label>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                className="cancel-button"
                                onClick={() => setShowClientModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="generate-button"
                                onClick={generatePDF}
                                disabled={!clientInfo.clientName || !clientInfo.companyName}
                            >
                                Generate PDF
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PDFExport;
